import React, { useState, useEffect, useCallback, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import "../order/ManageOrderFilters.css";
import { useSelector, useDispatch } from "react-redux";
import axiosInstance from "../../config/axiosApi/axiosApi";
import APIs from "../../Constants/apis";
import Preloader from "../../components/CustomComponents/preLoad/preLoader";
import showErrorToast, { showSuccessToast } from "../../components/CustomComponents/toast/toast";
import useDarkMode from "../../hooks/useDarkMode";
import useThemeColor from "../../hooks/useThemeColor";
import { LazyLoadImage } from "react-lazy-load-image-component";
import swal from "sweetalert";
import Select from "react-select";
import DropDownStyles, { DropDownStylesDark } from "../staff/dropdownStyles";
import {
    setVisibleColumns,
    setSortOrder,
    setSortBy,
    setFilterField,
} from "../../app/itemGridPreferencesSlice";
import { mergeVisibleColumns, needsPreferencesUpdate } from "../../utils/preferencesUtils";
import { MANAGE_PAGE_SEARCH_DEBOUNCE_MS } from "../../utils/managePageConstants";
import ColumnSelection from "../../components/CustomComponents/ColumnSelection/ColumnSelection";
import Pagination from "../../components/CustomComponents/Pagination/pagination.js";

const ManagePurchaseOrders = () => {
    const navigate = useNavigate();
    const compID = useSelector((state) => state.auth.user?.userData?.compID);
    const dispatch = useDispatch();
    const isDarkMode = useDarkMode();
    const themeColor = useThemeColor();
    const dropdownStyles = isDarkMode ? DropDownStylesDark : DropDownStyles;

    const [loading, setLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [initialLoading, setInitialLoading] = useState(true);
    const [purchaseOrders, setPurchaseOrders] = useState([]);
    const [originalItemsCount, setOriginalItemsCount] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [totalRecords, setTotalRecords] = useState(0);
    const [pageNumber, setPageNumber] = useState(1);
    const [selectedItems, setSelectedItems] = useState(new Set());
    const [supplierOptions, setSupplierOptions] = useState([{ value: "", label: "All Suppliers" }]);
    const lastSelectedIndex = useRef(null);

    // Page size state
    const [pageSize, setPageSize] = useState({ value: "10", label: "10" });

    // Page key for preferences
    const pageKey = "purchaseOrders";

    // Default visible columns
    const defaultVisibleColumns = {
        poNumber: true,
        poName: true,
        supplier: true,
        status: true,
        paymentStatus: true,
        amount: true,
        paidAmount: true,
        date: true,
    };

    const defaultSortOrder = { value: "DESC", label: "DESC" };
    const defaultSortBy = { value: "CreatedDate", label: "Date Created" };

    // Filter options
    const defaultStatus = { value: "", label: "All Status" };
    const defaultSupplier = { value: "", label: "All Suppliers" };

    const preferences = useSelector(
        (state) => state.itemGridPreferences[pageKey] || {}
    );
    const visibleColumns = mergeVisibleColumns(preferences.visibleColumns, defaultVisibleColumns);
    const sortOrder = preferences.sortOrder || defaultSortOrder;
    const sortBy = preferences.sortBy || defaultSortBy;

    // Filter states
    const status = preferences.status || defaultStatus;
    const supplier = preferences.supplier || defaultSupplier;

    const sortByOptions = [
        { value: "CreatedDate", label: "Date Created" },
        { value: "PO_ID", label: "PO ID" },
        { value: "PO_Name", label: "PO Name" },
        { value: "PO_Amount", label: "Amount" },
    ];

    const statusOptions = [
        { value: "", label: "All Status" },
        { value: "Draft", label: "Draft" },
        { value: "Pending", label: "Pending Approval" },
        { value: "Approved", label: "Approved" },
        { value: "Ordered", label: "Ordered" },
        { value: "Received", label: "Received" },
        { value: "Cancelled", label: "Cancelled" },
    ];

    // Search term state
    const [search, setSearch] = useState("");

    // Fetch suppliers for filter dropdown
    const fetchSuppliers = useCallback(async () => {
        if (!compID) return;
        try {
            // Use full company list (not paginated) so filter options are complete
            const response = await axiosInstance.get(APIs.GET_ALL_SUPPLIERS_BY_COMPANY, {
                params: { CompID: compID },
            });
            const supplierData = response.data?.data ?? response.data ?? [];
            const suppliers = (Array.isArray(supplierData) ? supplierData : [])
                .map((s) => ({
                    value: s.S_ID ?? s.s_ID ?? s.id,
                    label: s.S_Name ?? s.s_Name ?? s.name ?? `Supplier ${s.S_ID ?? s.s_ID ?? ""}`,
                }))
                .filter((o) => o.value !== undefined && o.value !== null && o.value !== "");
            setSupplierOptions([{ value: "", label: "All Suppliers" }, ...suppliers]);
        } catch (error) {
            console.error("Error fetching suppliers:", error);
        }
    }, [compID]);

    // Fetch purchase orders
    const fetchPurchaseOrders = useCallback(async () => {
        if (!compID) return;

        try {
            setLoading(true);
            const response = await axiosInstance.get(`api/PurchaseOrder/GetPurchaseOrdersByCompany`, {
                params: {
                    compId: compID,
                    pageNumber: pageNumber,
                    pageSize: pageSize.value,
                    Search: search?.trim() || "",
                    SortBy: sortBy.value,
                    SortOrder: sortOrder.value,
                    Status: status?.value !== "" ? String(status?.value) : "",
                    SupplierID: supplier?.value !== "" ? supplier?.value : ""
                }
            });

            const ordersData = response.data.data || response.data;

            setTotalPages(response.data.totalPages || 0);
            setTotalRecords(response.data.totalRecords || 0);
            if (Array.isArray(ordersData)) {
                setPurchaseOrders(ordersData);
                if (!search.trim() && status?.value === "" && supplier?.value === "") {
                    setOriginalItemsCount(ordersData.length);
                }
            } else {
                setPurchaseOrders([]);
            }
            setLoading(false);
            setInitialLoading(false);
        } catch (error) {
            console.error("Error fetching purchase orders:", error);
            showErrorToast("Failed to load purchase orders");
            setLoading(false);
            setInitialLoading(false);
            setPurchaseOrders([]);
        }
    }, [compID, pageNumber, pageSize, search, sortBy, sortOrder, status, supplier]);

    useEffect(() => {
        // Check if preferences exist and merge with defaults to ensure new columns are included
        if (!preferences.visibleColumns) {
            dispatch(setVisibleColumns({ pageKey, columns: defaultVisibleColumns }));
        } else {
            // Check if preferences need updating (missing new columns or have removed columns)
            if (needsPreferencesUpdate(preferences.visibleColumns, defaultVisibleColumns)) {
                // Merge existing preferences with default columns (this handles both additions and removals)
                const mergedColumns = mergeVisibleColumns(preferences.visibleColumns, defaultVisibleColumns);
                dispatch(setVisibleColumns({ pageKey, columns: mergedColumns }));
            }
        }

        if (!preferences.sortOrder) {
            dispatch(setSortOrder({ pageKey, sortOrder: defaultSortOrder }));
        }
        if (!preferences.sortBy) {
            dispatch(setSortBy({ pageKey, sortBy: defaultSortBy }));
        }

        // Initialize filter states
        if (!preferences.status) {
            dispatch(setFilterField({ pageKey, field: "status", value: defaultStatus }));
        }
        if (!preferences.supplier) {
            dispatch(setFilterField({ pageKey, field: "supplier", value: defaultSupplier }));
        }

        // Fetch suppliers and purchase orders on component mount
        fetchSuppliers();
    }, [dispatch, pageKey, fetchSuppliers]);

    useEffect(() => {
        const handler = setTimeout(() => {
            fetchPurchaseOrders();
        }, MANAGE_PAGE_SEARCH_DEBOUNCE_MS);
        return () => clearTimeout(handler);
    }, [fetchPurchaseOrders]);

    // Handle item selection
    const handleSelectItem = (itemId, index, event) => {
        setSelectedItems((prev) => {
            const newSelected = new Set(prev);

            if (event.shiftKey && lastSelectedIndex.current !== null) {
                const start = Math.min(lastSelectedIndex.current, index);
                const end = Math.max(lastSelectedIndex.current, index);

                for (let i = start; i <= end; i++) {
                    if (purchaseOrders[i]?.po_ID) {
                        newSelected.add(purchaseOrders[i].po_ID);
                    }
                }
            } else {
                if (newSelected.has(itemId)) {
                    newSelected.delete(itemId);
                } else {
                    newSelected.add(itemId);
                }
                lastSelectedIndex.current = index;
            }

            return newSelected;
        });
    };

    const handleSelectAll = (e) => {
        if (e.target.checked) {
            const newSelected = new Set(purchaseOrders.map((order) => order.po_ID));
            setSelectedItems(newSelected);
            lastSelectedIndex.current = null;
        } else {
            setSelectedItems(new Set());
            lastSelectedIndex.current = null;
        }
    };

    const handleDeselectAll = () => {
        setSelectedItems(new Set());
        lastSelectedIndex.current = null;
    };

    // Handle bulk delete
    const handleBulkDelete = async () => {
        if (selectedItems.size === 0) {
            showErrorToast("No purchase orders selected for deletion.");
            return;
        }

        try {
            const confirmDelete = await swal({
                title: "Are you sure?",
                text: `Are you sure you want to delete ${selectedItems.size} selected purchase order(s)?`,
                icon: "warning",
                buttons: true,
                dangerMode: true,
            });

            if (!confirmDelete) return;

            setLoading(true);
            await Promise.all(
                Array.from(selectedItems).map((poId) =>
                    axiosInstance.delete(`${APIs.BASE_URL}PurchaseOrder/DeletePurchaseOrder?poId=${poId}&compId=${compID}`)
                )
            );

            setSelectedItems(new Set());
            lastSelectedIndex.current = null;
            fetchPurchaseOrders();
            showSuccessToast(`${selectedItems.size} purchase order(s) deleted successfully.`);
            setLoading(false);
        } catch (err) {
            showErrorToast("Failed to delete selected purchase orders.");
            setLoading(false);
        }
    };

    // Clear filters
    const clearFilters = () => {
        setSearch("");
        dispatch(setFilterField({ pageKey, field: "status", value: defaultStatus }));
        dispatch(setFilterField({ pageKey, field: "supplier", value: defaultSupplier }));
        setPageNumber(1);
    };

    // Check if any filter is applied (excluding search)
    const hasFiltersApplied = () => {
        return (
            (status && status.value !== "") ||
            (supplier && supplier.value !== "")
        );
    };

    const renderNoDataMessage = () => {
        if (purchaseOrders.length === 0 && (search.trim() !== "" || hasFiltersApplied())) {
            return (
                <div className="alert alert-info text-center mt-3">
                    <i className="fa fa-info-circle me-2"></i>
                    No purchase orders found matching your filter criteria.
                </div>
            );
        }
        return null;
    };

    // Handle delete purchase order
    const handleDeleteOrder = async (order) => {
        const confirmDelete = await swal({
            title: "Are you sure?",
            text: `Are you sure you want to delete purchase order "${order.po_Name}" (PO-${order.po_ID})? This action cannot be undone.`,
            icon: "warning",
            buttons: true,
            dangerMode: true,
        });

        if (!confirmDelete) return;

        try {
            setLoading(true);
            await axiosInstance.delete(`${APIs.BASE_URL}PurchaseOrder/DeletePurchaseOrder?poId=${order.po_ID}&compId=${compID}`);
            showSuccessToast("Purchase order deleted successfully");
            fetchPurchaseOrders(currentPage);
        } catch (error) {
            console.error("Error deleting purchase order:", error);
            showErrorToast("Failed to delete purchase order");
        } finally {
            setLoading(false);
        }
    };

    // Get status color
    const getStatusColor = (status) => {
        switch (status?.toLowerCase()) {
            case 'draft': return '#6c757d';
            case 'pending': return '#ffc107';
            case 'approved': return '#17a2b8';
            case 'ordered': return '#007bff';
            case 'received': return '#28a745';
            case 'cancelled': return '#dc3545';
            default: return '#6c757d';
        }
    };

    // Get payment status color
    const getPaymentStatusColor = (status) => {
        switch (status?.toLowerCase()) {
            case 'unpaid': return '#dc3545';
            case 'partial': return '#ffc107';
            case 'paid': return '#28a745';
            default: return '#6c757d';
        }
    };

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    if (initialLoading) {
        return (
            <div className="container-fluid p-3">
                <Preloader />
            </div>
        );
    }

    return (
        <div>
            {loading && <Preloader />}
            <div className="d-flex align-items-center mb-3">
                <div>
                    <ol className="breadcrumb">
                    <li className="breadcrumb-item">
                        <Link to="/dashboard">Home</Link>
                    </li>
                        <li className="breadcrumb-item active">Purchase Orders</li>
                    </ol>
                    <h1 className="page-header mb-0">
                        <i className="fa fa-shopping-cart me-2"></i>
                        Purchase Orders
                    </h1>
                </div>
                <div className="ms-auto">
                    <button
                        className="btn px-4"
                        onClick={() => navigate("/purchaseOrder/createPurchaseOrder")}
                        style={{
                            backgroundColor: themeColor,
                            background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
                            border: "none",
                            color: "white",
                            borderRadius: "8px",
                            padding: "8px 16px",
                            fontSize: "14px",
                            fontWeight: "600",
                            transition: "all 0.3s ease",
                            boxShadow: `0 2px 8px ${themeColor}40`,
                        }}
                        onMouseEnter={(e) => {
                            e.target.style.background = `linear-gradient(135deg, ${themeColor}dd 0%, ${themeColor} 100%)`;
                            e.target.style.transform = "translateY(-1px)";
                            e.target.style.boxShadow = `0 4px 12px ${themeColor}60`;
                        }}
                        onMouseLeave={(e) => {
                            e.target.style.background = `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`;
                            e.target.style.transform = "translateY(0)";
                            e.target.style.boxShadow = `0 2px 8px ${themeColor}40`;
                        }}
                    >
                        <i className="fas fa-plus fa-lg me-2 ms-n2 text-white"></i>
                        Create Purchase Order
                    </button>
                </div>
            </div>

            {/* Filter Panel Section - Match ManageItems layout */}
            <div className="mb-3 mt-2">
                <div className="card border-0">
                    <div className="tab-content p-3">
                        <div className="tab-pane fade show active" id="allTab">
                            {/* Enhanced Filter Section */}
                            <div className="mb-4">
                                <div
                                    className="card filter-card"
                                    style={{
                                        position: "relative",
                                        zIndex: 1,
                                        background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
                                        borderRadius: "12px",
                                        border: "none",
                                        boxShadow: `0 4px 20px ${themeColor}40`,
                                    }}
                                >
                                    <div className="card-body p-3">
                                        {/* Filter Header */}

                                        <div className="row g-3 align-items-center">
                                            {/* Search Field */}
                                            <div className="col-xl-3 col-lg-3 col-md-6">
                                                <div className="search-input filter-control">
                                                    <input
                                                        type="text"
                                                        className="filter-input"
                                                        value={search}
                                                        onChange={(e) => {
                                                            setSearch(e.target.value);
                                                            setPageNumber(1);
                                                        }}
                                                        placeholder="Search by PO name, supplier, or ID..."
                                                        style={{
                                                            backgroundColor: isDarkMode ? "#2d3436" : "rgba(255, 255, 255, 0.9)",
                                                            border: isDarkMode ? "1px solid #404040" : "1px solid rgba(255, 255, 255, 0.3)",
                                                            color: isDarkMode ? "#ffffff" : "#333",
                                                            padding: "8px 12px",
                                                            borderRadius: "6px",
                                                            outline: "none",
                                                        }}
                                                    />
                                                </div>
                                            </div>

                                            {/* Status Dropdown */}
                                            <div className="col-xl-2 col-lg-2 col-md-6">
                                                <div className="filter-control">
                                                    <Select
                                                        value={status}
                                                        onChange={(val) => {
                                                            dispatch(
                                                                setFilterField({
                                                                    pageKey,
                                                                    field: "status",
                                                                    value: val,
                                                                })
                                                            );
                                                            setPageNumber(1);
                                                        }}
                                                        options={statusOptions}
                                                        styles={dropdownStyles}
                                                        placeholder="All Status"
                                                        className="filter-dropdown"
                                                        menuPortalTarget={document.body}
                                                        menuPosition="fixed"
                                                        menuShouldBlockScroll={false}
                                                        menuShouldScrollIntoView={false}
                                                        closeMenuOnScroll={false}
                                                    />
                                                </div>
                                            </div>

                                            {/* Supplier Dropdown */}
                                            <div className="col-xl-2 col-lg-2 col-md-6">
                                                <div className="filter-control">
                                                    <Select
                                                        value={supplier}
                                                        onChange={(val) => {
                                                            dispatch(
                                                                setFilterField({
                                                                    pageKey,
                                                                    field: "supplier",
                                                                    value: val,
                                                                })
                                                            );
                                                            setPageNumber(1);
                                                        }}
                                                        options={supplierOptions}
                                                        styles={dropdownStyles}
                                                        placeholder="All Suppliers"
                                                        className="filter-dropdown"
                                                        menuPortalTarget={document.body}
                                                        menuPosition="fixed"
                                                        menuShouldBlockScroll={false}
                                                        menuShouldScrollIntoView={false}
                                                        closeMenuOnScroll={false}
                                                    />
                                                </div>
                                            </div>

                                            {/* Order By Dropdown */}
                                            <div className="col-xl-2 col-lg-2 col-md-6">
                                                <div className="filter-control">
                                                    <Select
                                                        value={sortBy}
                                                        onChange={(val) =>
                                                            dispatch(setSortBy({ pageKey, sortBy: val }))
                                                        }
                                                        options={sortByOptions}
                                                        styles={dropdownStyles}
                                                        placeholder="Order By"
                                                        className="filter-dropdown"
                                                        menuPortalTarget={document.body}
                                                        menuPosition="fixed"
                                                        menuShouldBlockScroll={false}
                                                        menuShouldScrollIntoView={false}
                                                        closeMenuOnScroll={false}
                                                    />
                                                </div>
                                            </div>

                                            {/* Sort Order Dropdown */}
                                            <div className="col-xl-1 col-lg-1 col-md-6">
                                                <div className="filter-control">
                                                    <div className="d-flex">
                                                        <button
                                                            className={`btn ${sortOrder.value === "ASC" ? "btn-light" : "btn-outline-light"} me-1`}
                                                            onClick={() => dispatch(setSortOrder({ pageKey, sortOrder: { value: "ASC", label: "ASC" } }))}
                                                            style={{ padding: "6px 12px" }}
                                                        >
                                                            ↑
                                                        </button>
                                                        <button
                                                            className={`btn ${sortOrder.value === "DESC" ? "btn-light" : "btn-outline-light"}`}
                                                            onClick={() => dispatch(setSortOrder({ pageKey, sortOrder: { value: "DESC", label: "DESC" } }))}
                                                            style={{ padding: "6px 12px" }}
                                                        >
                                                            ↓
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Action Buttons */}
                                            <div className="col-xl-2 col-lg-2 col-md-12">
                                                <div className="d-flex gap-2">
                                                    <button
                                                        className="clear-btn"
                                                        onClick={clearFilters}
                                                        title="Clear all filters"
                                                    >
                                                        <i className="fa fa-times me-1"></i>
                                                        Filter
                                                    </button>

                                                    <ColumnSelection
                                                        visibleColumns={visibleColumns}
                                                        setVisibleColumns={(cols) =>
                                                            dispatch(setVisibleColumns({ pageKey, columns: cols }))
                                                        }
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Show centered Add Purchase Order button only when no items exist and no filters are applied */}
            {!loading && purchaseOrders.length === 0 && search.trim() === "" && !hasFiltersApplied() && originalItemsCount === 0 && (
                <div className="text-center py-4">
                    <button
                        className="btn px-4"
                        onClick={() => navigate("/purchaseOrder/createPurchaseOrder")}
                        style={{
                            backgroundColor: themeColor,
                            background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
                            border: "none",
                            color: "white",
                            borderRadius: "8px",
                            padding: "12px 24px",
                            fontSize: "16px",
                            fontWeight: "600",
                            transition: "all 0.3s ease",
                            boxShadow: `0 2px 8px ${themeColor}40`,
                        }}
                        onMouseEnter={(e) => {
                            e.target.style.background = `linear-gradient(135deg, ${themeColor}dd 0%, ${themeColor} 100%)`;
                            e.target.style.transform = "translateY(-1px)";
                            e.target.style.boxShadow = `0 4px 12px ${themeColor}60`;
                        }}
                        onMouseLeave={(e) => {
                            e.target.style.background = `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`;
                            e.target.style.transform = "translateY(0)";
                            e.target.style.boxShadow = `0 2px 8px ${themeColor}40`;
                        }}
                    >
                        <i className="fas fa-plus fa-lg me-2 ms-n2 text-white"></i>
                        Create Your First Purchase Order
                    </button>
                </div>
            )}

            {/* Purchase Orders Table */}
            {(!loading && (purchaseOrders.length > 0 || search.trim() !== "" || hasFiltersApplied() || originalItemsCount > 0)) && (
                <>
                    {selectedItems.size > 0 && (
                        <div className="mb-3">
                            <div className="p-3 bg-light rounded d-flex align-items-center justify-content-between" style={{ border: "1px solid #dee2e6" }}>
                                <div className="d-flex align-items-center gap-3">
                                    <span className="fw-bold text-primary">
                                        {selectedItems.size} purchase order{selectedItems.size > 1 ? 's' : ''} selected
                                    </span>
                                    <button
                                        className="btn btn-sm btn-outline-secondary"
                                        onClick={handleDeselectAll}
                                    >
                                        Clear Selection
                                    </button>
                                </div>

                                <div className="d-flex gap-2">
                                    <button
                                        className="btn btn-sm btn-danger"
                                        onClick={handleBulkDelete}
                                        title="Delete Selected Purchase Orders"
                                    >
                                        <i className="fa fa-trash me-1"></i>
                                        Delete
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {renderNoDataMessage()}

                    {Array.isArray(purchaseOrders) && purchaseOrders.length > 0 && (
                        <>
                            <div
                                className="table-responsive table-container"
                                style={{
                                    minHeight: "200px",
                                    maxHeight: "450px",
                                    overflowY: "auto",
                                    overflowX: "auto",
                                    position: "relative",
                                    zIndex: 1,
                                }}
                            >
                                <table className="table table-hover" style={{ position: "relative" }}>
                                    <thead
                                        className="bg-light sticky-header"
                                        style={{
                                            position: "sticky",
                                            top: 0,
                                            zIndex: 10000,
                                            backgroundColor: isDarkMode ? "#2d3436" : "#f8f9fa",
                                            color: isDarkMode ? "#ffffff" : "#000000",
                                        }}
                                    >
                                        <tr>
                                            <th className="pt-2 pb-2">
                                                <input
                                                    type="checkbox"
                                                    onChange={handleSelectAll}
                                                    checked={
                                                        purchaseOrders.length > 0 &&
                                                        selectedItems.size === purchaseOrders.length
                                                    }
                                                />
                                            </th>
                                            {visibleColumns.poNumber && (
                                                <th className="pt-2 pb-2">PO #</th>
                                            )}
                                            {visibleColumns.poName && (
                                                <th className="pt-2 pb-2">Name</th>
                                            )}
                                            {visibleColumns.supplier && (
                                                <th className="pt-2 pb-2">Supplier</th>
                                            )}
                                            {visibleColumns.status && (
                                                <th className="pt-2 pb-2 text-center">Status</th>
                                            )}
                                            {visibleColumns.paymentStatus && (
                                                <th className="pt-2 pb-2 text-center">Payment</th>
                                            )}
                                            {visibleColumns.amount && (
                                                <th className="pt-2 pb-2 text-center">Amount</th>
                                            )}
                                            {visibleColumns.paidAmount && (
                                                <th className="pt-2 pb-2 text-center">Paid Amount</th>
                                            )}
                                            {visibleColumns.date && (
                                                <th className="pt-2 pb-2 text-center">Date</th>
                                            )}
                                            <th className="pt-2 pb-2 text-center">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {purchaseOrders.map((order, index) => (
                                            <tr
                                                key={order.pO_ID}
                                                draggable={false}
                                                onDragEnter={(e) => handleSelectItem(order.pO_ID, index, { shiftKey: false })}
                                            >
                                                <td
                                                    className="align-middle"
                                                >
                                                    <input
                                                        type="checkbox"
                                                        checked={selectedItems.has(order.pO_ID)}
                                                        onChange={(e) =>
                                                            handleSelectItem(order.pO_ID, index, e)
                                                        }
                                                        data-chkbox-shiftsel="type1"
                                                    />
                                                </td>

                                                {visibleColumns.poNumber && (
                                                    <td
                                                        className="align-middle"
                                                        onClick={() =>
                                                            navigate(`/purchaseOrder/updatePurchaseOrder/${order.pO_ID}`)
                                                        }
                                                        style={{ cursor: "pointer" }}
                                                    >
                                                        PO-{order.pO_ID}
                                                    </td>
                                                )}

                                                {visibleColumns.poName && (
                                                    <td
                                                        className="align-middle"
                                                        onClick={() =>
                                                            navigate(`/purchaseOrder/updatePurchaseOrder/${order.pO_ID}`)
                                                        }
                                                        style={{ cursor: "pointer" }}
                                                    >
                                                        <div>
                                                            <div style={{ fontWeight: "500" }}>{order.pO_Name}</div>
                                                            {order.staffUserName && (
                                                                <small style={{ color: isDarkMode ? "#a0aec0" : "#6c757d" }}>
                                                                    by {order.staffUserName}
                                                                </small>
                                                            )}
                                                        </div>
                                                    </td>
                                                )}

                                                {visibleColumns.supplier && (
                                                    <td
                                                        className="align-middle"
                                                        onClick={() =>
                                                            navigate(`/purchaseOrder/updatePurchaseOrder/${order.pO_ID}`)
                                                        }
                                                        style={{ cursor: "pointer" }}
                                                    >
                                                        {order.supplierName || "N/A"}
                                                    </td>
                                                )}

                                                {visibleColumns.status && (
                                                    <td
                                                        className="align-middle text-center"
                                                        onClick={() =>
                                                            navigate(`/purchaseOrder/updatePurchaseOrder/${order.pO_ID}`)
                                                        }
                                                        style={{ cursor: "pointer" }}
                                                    >
                                                        <span
                                                            className="badge"
                                                            style={{
                                                                backgroundColor: getStatusColor(order.pO_status),
                                                                color: "white",
                                                                padding: "4px 8px",
                                                                borderRadius: "4px",
                                                                fontSize: "11px",
                                                                fontWeight: "500",
                                                            }}
                                                        >
                                                            {order.pO_status || "Draft"}
                                                        </span>
                                                    </td>
                                                )}

                                                {visibleColumns.paymentStatus && (
                                                    <td
                                                        className="align-middle text-center"
                                                        onClick={() =>
                                                            navigate(`/purchaseOrder/updatePurchaseOrder/${order.pO_ID}`)
                                                        }
                                                        style={{ cursor: "pointer" }}
                                                    >
                                                        <span
                                                            className="badge"
                                                            style={{
                                                                backgroundColor: getPaymentStatusColor(order.pO_Paymentstatus),
                                                                color: "white",
                                                                padding: "4px 8px",
                                                                borderRadius: "4px",
                                                                fontSize: "11px",
                                                                fontWeight: "500",
                                                            }}
                                                        >
                                                            {order.pO_Paymentstatus || "Unpaid"}
                                                        </span>
                                                    </td>
                                                )}

                                                {visibleColumns.amount && (
                                                    <td
                                                        className="align-middle text-center"
                                                        onClick={() =>
                                                            navigate(`/purchaseOrder/updatePurchaseOrder/${order.pO_ID}`)
                                                        }
                                                        style={{ cursor: "pointer" }}
                                                    >
                                                        ${order.pO_Amount?.toFixed(2) || "0.00"}
                                                    </td>
                                                )}

                                                {visibleColumns.paidAmount && (
                                                    <td
                                                        className="align-middle text-center"
                                                        onClick={() =>
                                                            navigate(`/purchaseOrder/updatePurchaseOrder/${order.pO_ID}`)
                                                        }
                                                        style={{ cursor: "pointer" }}
                                                    >
                                                        ${order.pO_PaidAmount?.toFixed(2) || "0.00"}
                                                    </td>
                                                )}

                                                {visibleColumns.date && (
                                                    <td
                                                        className="align-middle text-center"
                                                        onClick={() =>
                                                            navigate(`/purchaseOrder/updatePurchaseOrder/${order.pO_ID}`)
                                                        }
                                                        style={{ cursor: "pointer" }}
                                                    >
                                                        {order.createdDate ? new Date(order.createdDate).toLocaleDateString() : "N/A"}
                                                    </td>
                                                )}

                                                <td className="align-middle text-center">
                                                    <div className="d-flex justify-content-center gap-2">
                                                        <Link
                                                            to={`/purchaseOrder/updatePurchaseOrder/${order.pO_ID}`}
                                                            className="btn btn-sm btn-outline-primary"
                                                            title="Update Purchase Order"
                                                            style={{
                                                                width: '32px',
                                                                height: '32px',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                borderRadius: '6px',
                                                                border: `1px solid ${themeColor}`,
                                                                color: themeColor,
                                                                transition: 'all 0.2s ease'
                                                            }}
                                                            onMouseEnter={(e) => {
                                                                e.target.style.backgroundColor = themeColor;
                                                                e.target.style.color = 'white';
                                                                e.target.style.transform = 'scale(1.05)';
                                                            }}
                                                            onMouseLeave={(e) => {
                                                                e.target.style.backgroundColor = 'transparent';
                                                                e.target.style.color = themeColor;
                                                                e.target.style.transform = 'scale(1)';
                                                            }}
                                                        >
                                                            <i className="fa fa-edit" style={{ fontSize: '12px' }}></i>
                                                        </Link>

                                                        <button
                                                            onClick={() => handleDeleteOrder(order)}
                                                            className="btn btn-sm btn-outline-danger"
                                                            title="Delete Purchase Order"
                                                            disabled={loading}
                                                            style={{
                                                                width: '32px',
                                                                height: '32px',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                borderRadius: '6px',
                                                                border: '1px solid #dc3545',
                                                                color: '#dc3545',
                                                                transition: 'all 0.2s ease'
                                                            }}
                                                            onMouseEnter={(e) => {
                                                                e.target.style.backgroundColor = '#dc3545';
                                                                e.target.style.color = 'white';
                                                                e.target.style.transform = 'scale(1.05)';
                                                            }}
                                                            onMouseLeave={(e) => {
                                                                e.target.style.backgroundColor = 'transparent';
                                                                e.target.style.color = '#dc3545';
                                                                e.target.style.transform = 'scale(1)';
                                                            }}
                                                        >
                                                            <i className="fa fa-trash" style={{ fontSize: '12px' }}></i>
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </>
                    )}

                    <Pagination
                        totalRecords={totalRecords}
                        pageSize={pageSize}
                        pageNumber={pageNumber}
                        totalPages={totalPages}
                        setPageNumber={setPageNumber}
                        setPageSize={setPageSize}
                        themeColor={themeColor}
                    />
                </>

            )}

        </div>
    );
};

export default ManagePurchaseOrders;